import React, { SetStateAction, useRef, useEffect } from 'react'
import { Children } from '../../custom-types'
import { ButtonWidget } from '../../elementor'
import { v4 as uuidv4 } from 'uuid'
interface ModalI {
  children?: Children
  mainContent?: Children
  fullScreen?: boolean
  showCloseButton?: boolean
  size?: string
  heading?: string
  subheading?: string
  contentWidth?: string
  modalClass?: string
  dialogPosition?: string
  headingAlignment?: string
  setDismissalState: React.Dispatch<SetStateAction<boolean>>
  actionButton?: ButtonWidget
}

const Modal: React.FC<ModalI> = ({ ...props }) => {
  const _uid = uuidv4()
  const modalRef = useRef(null)
  const modalTrapFocus = useRef(null)
  const focusSpan = useRef<HTMLElement>(null)
  const storedY = useRef(0)
  const storedPreviousActiveElement = useRef<HTMLElement | null>(null)
  const dialogPositionClass = typeof props.dialogPosition === 'undefined' ? 'modal-vertical-center' : props.dialogPosition === 'vertical-center' ? 'modal-vertical-center' : ''
  const headingAlignmentClass = props.headingAlignment === 'left' ? 'modal-header-align-left' : ''
  const setY = () => storedY.current = window.scrollY
  const preventDocumentScroll = () => {
    document.documentElement.setAttribute(
      'style',
      `position: fixed; top: ${-1 *
        storedY.current}px; height: 100%; width: 100%; overflow-y: scroll;`
    )
  }
  const returnFocusOnTrigger = () => storedPreviousActiveElement?.current?.focus()

  useEffect(() => {
    focusSpan?.current?.focus()
    setY()
    preventDocumentScroll()
    if (document.activeElement instanceof HTMLElement) storedPreviousActiveElement.current = document.activeElement
  }, [window.scrollY])

  useEffect(() => {
    return () => {
      document.documentElement.setAttribute('style', 'position: relative;')
      window.scrollTo(0, storedY.current)
      returnFocusOnTrigger()
    }
  }, [window.scrollY])

  return (
    <div
      role='dialog'
      tabIndex={-1}
      aria-labelledby={`modalHeading${_uid}`}
      aria-describedby={`modalDescription${_uid}`}
      aria-modal='true'
      className={`modal-overlay ${dialogPositionClass} ${props.modalClass || ''}`}
    >
      <div
        className={`modal-wrapper ${props.fullScreen ? 'modal-wrapper-fullscreen' : ''}`}
        onClick={() => props.setDismissalState(false)}
        onKeyDown={() => props.setDismissalState(false)}
      >
        <div className='modal-document-wrapper'>
          <div
            ref={modalRef}
            role='document'
            className={`modal-dialog ${props.fullScreen ? 'modal-dialog-fullscreen' : `modal-dialog-${props.size || 'medium'}`}`}
            onClick={e => { e.preventDefault(); e.stopPropagation() }}
          >
            <span
              ref={focusSpan}
              tabIndex={0}
              aria-label='Beginning of this dialog window'
              className='modal-focus-trap'
              onKeyDown={e => { if (e.key === 'Tab' || e.key === 'Escape') { e.preventDefault(); e.stopPropagation() } }}
            />
            <header className={`modal-header ${headingAlignmentClass}`}>
              <slot name='modal-header'>
                <h5 id={`modalHeading${_uid}`} className='modal-heading'>
                  {props.heading}
                </h5>
                <div className='modal-subheading'>
                  {props.subheading}
                </div>
              </slot>
            </header>

            <div
              id={`modalDescription${_uid}`}
              className={`modal-body ${props.contentWidth ? `modal-body-${props.contentWidth}` : ''}`}
            >
              {props.mainContent}
            </div>

            <span
              ref={modalTrapFocus}
              tabIndex={0}
              aria-label='End of this dialog window'
              className='modal-focus-trap'
              onKeyDown={e => { if (e.key === 'Tab' || e.key === 'Escape') { e.preventDefault(); e.stopPropagation() } }}
            />
          </div>
          {(props.actionButton != null) &&
            <a
              href={props.actionButton.link}
            >
              <button className='rula-button black modal-action-button'>{props.actionButton.text}</button>
            </a>}
          {props.showCloseButton &&
            <button
              type='button'
              className='modal-close-button'
              aria-label='Close this dialog window'
              onClick={() => props.setDismissalState(false)}
            >
              <svg
                width='22'
                height='22'
                viewBox='0 0 22 22'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M21 21L1 1M21 1L1 21'
                  stroke='black'
                  strokeWidth='3'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>}
        </div>
      </div>
    </div>
  )
}

export default Modal
